import React, { useState, useCallback, useEffect, useRef } from 'react';
import s from './s.module.less';
import EmailOnboardingStep from './components/EmailOnboardingStep';
import HelpTypeStep from './components/HelpTypeStep';
import ServiceSelectionStep from './components/ServiceSelectionStep';
import LicenseSelectionStep from './components/LicenseSelectionStep';
import PracticeSizeStep from './components/PracticeSizeStep';
import PatientVolumeStep from './components/PatientVolumeStep';
import ReferralSourceStep from './components/ReferralSourceStep';
import EhrAccessStep from './components/EhrAccessStep';
import BillingMethodStep from './components/BillingMethodStep';
import NoEhrInfoStep from './components/NoEhrInfoStep';
import AIChat from 'components/AIChat';
import UserNotQualified from './components/UserNotQualified/index';
import PPAOnboardingHeader from 'components/PPAOnboardingHeader';
import {
    saveLeadFormStep,
    TLeadFormStepData,
    getLeadFormByEmail,
    checkLeadFormByEmail,
    TCheckLeadFormByEmailResponse,
} from 'api/ppaLeadForm';
import { EAIChatPageId } from 'types/ai';
import SignUp from '../PPASignUpPage';
import { ConfigProvider } from 'antd';
import { PATH } from 'constants/path';
import { useNavigate } from 'react-router-dom';
import { isMobile, getUrlParam, buildUtmObject } from 'utils/common';
import { trackHeapEvent } from 'utils/analytics';
import { encryptString } from 'utils/encrypt';
import { setCurrentPlatformType } from 'utils/sessionStorage';
import { ERegisterType } from 'api/public';
import cx from 'classnames';
import { EPPAStep } from 'types/ppaOnboarding';

export default function PPALeadFormPage() {
    const navigator = useNavigate();
    const [currentStep, setCurrentStep] = useState(1);
    const [loading, setLoading] = useState(false);

    // 从URL参数中获取邮箱和action
    const urlEmail = getUrlParam('email') || '';
    const urlAction = getUrlParam('action') || '';

    const [formData, setFormData] = useState<TLeadFormStepData>({
        email: urlEmail,
        states: '',
        providerCount: '',
        patientsPerMonth: '',
        referralSource: '',
        billingMethod: '',
        ehrSystemStatus: '',
        practiceHelpType: '',
        specialties: [] as string[],
        step: 1,
        providerId: '',
    });
    const [isNotQualified, setIsNotQualified] = useState(false);
    const [isCompleted, setIsCompleted] = useState(false);
    const [isGetAccountData, setIsGetAccountData] = useState(false);
    const [checkEmail, setCheckEmail] = useState('');
    const [isCheckingEmail, setIsCheckingEmail] = useState(false);
    const [checkEmailData, setCheckEmailData] = useState<TCheckLeadFormByEmailResponse | null>(null);
    const [hasAccount, setHasAccount] = useState(false);

    const [isChatVisible, setIsChatVisible] = useState(false);
    // 老账号补问 practiceHelpType 时记录原本要去的步骤，答完直接跳回，避免拖着重走 3→9
    const helpTypeResumeStepRef = useRef<number | null>(null);
    const saveStepData = useCallback(
        async (step: number, currentData: typeof formData) => {
            try {
                if (loading) {
                    return;
                }
                setLoading(true);
                const result = await saveLeadFormStep({
                    email: currentData.email,
                    providerId: currentData.providerId,
                    specialties: currentData.specialties,
                    states: currentData.states,
                    providerCount: currentData.providerCount,
                    patientsPerMonth: currentData.patientsPerMonth,
                    referralSource: currentData.referralSource,
                    ehrSystemStatus: currentData.ehrSystemStatus,
                    billingMethod: currentData.billingMethod,
                    // 未填写时不传：后端对 null 跳过、空串照写。传 "" 会把库里的
                    // NULL（没问过）覆盖成 ''（答过且为空），credentialing 分流随之失效
                    ...(currentData.practiceHelpType ? { practiceHelpType: currentData.practiceHelpType } : {}),
                    step,
                    utm: buildUtmObject(),
                });
                return result;
            } catch (error) {
                // eslint-disable-next-line no-console
                console.error(`Failed to save step ${step} data:`, error);
                return null;
            } finally {
                setLoading(false);
            }
        },
        [loading],
    );

    const totalSteps = 9;

    const evaluateQualificationAndMaybeStop = async (nextData: typeof formData, currentHasAccount?: boolean) => {
        // 使用传入的currentHasAccount参数，如果没有则使用当前的hasAccount状态
        const accountExists = currentHasAccount !== undefined ? currentHasAccount : hasAccount;

        // 是否存在账号，存在不显示注册页，而是直接去登录，避免二个密码
        if (accountExists) {
            navigator(PATH.PPA_LOGIN, {
                state: { redirect: PATH.DASHBOARD, email: nextData.email },
            });
        } else {
            setIsNotQualified(false);
        }
    };

    const handleEmailNext = async (nextData: typeof formData) => {
        await saveStepData(1, nextData);
        setFormData(nextData);
        setCurrentStep(2);
    };

    const getLeadFormInfoByEmail = async (email: string, currentHasAccount?: boolean) => {
        const { data } = await getLeadFormByEmail({ email });

        if (data.code === 200) {
            setIsGetAccountData(true);
            // 没填过信息，全新用户
            if (!data.data) {
                handleEmailNext({
                    ...formData,
                    email,
                });
                return;
            }

            // 填过lead-form
            // practiceHelpType 是后加到第 2 步的问题：老账号续填按 step+1 走会永久跳过它，
            // 而该字段决定登录后走 credentialing 还是 marketplace 分流，缺失即错判——缺就补问，
            // 答完跳回原本要去的步骤（resumeStepRef）
            const savedHelpType = data.data.practiceHelpType || '';
            if (!savedHelpType) {
                helpTypeResumeStepRef.current = urlAction === 'reselect' ? 8 : +data.data.step + 1;
                setCurrentStep(2);
            } else if (urlAction === 'reselect') {
                // 如果是action=reselect，直接跳转到 billing method 步骤（新增 help 问题后为第8步）
                setCurrentStep(8);
            } else {
                const nextStep = Math.min(+data.data.step + 1, totalSteps);
                setCurrentStep(nextStep);
            }
            // 解析specialties数据，根据后端返回的格式进行处理
            const parseSpecialties = (specialtiesData: unknown): string[] => {
                if (!specialtiesData) return [];

                // 如果已经是数组，直接返回
                if (Array.isArray(specialtiesData)) {
                    return specialtiesData;
                }

                // 如果是字符串，尝试解析
                if (typeof specialtiesData === 'string') {
                    try {
                        const parsed = JSON.parse(specialtiesData);
                        // 如果解析后还是字符串，再次解析
                        if (typeof parsed === 'string') {
                            return JSON.parse(parsed);
                        }
                        // 如果解析后是数组，直接返回
                        if (Array.isArray(parsed)) {
                            return parsed;
                        }
                    } catch (error) {
                        // eslint-disable-next-line no-console
                        console.error('Failed to parse specialties data:', error);
                        return [];
                    }
                }

                return [];
            };
            const parsedSpecialties = parseSpecialties(data.data.specialties);

            const result = {
                email: data.data.email || '',
                specialties: parsedSpecialties,
                states: data.data.states || '',
                providerCount: data.data.providerCount || '',
                patientsPerMonth: data.data.patientsPerMonth || '',
                referralSource: data.data.referralSource || '',
                ehrSystemStatus: urlAction === 'reselect' ? '' : data.data.ehrSystemStatus || '',
                billingMethod: data.data.billingMethod || '',
                practiceHelpType: data.data.practiceHelpType || '',
                step: data.data.step || 1,
                providerId: data.data.providerId || '',
            };

            setFormData(result);

            if (urlAction === 'reselect') {
                return;
            }

            // 提交过最后一步就是已经完成了lead form 流程，但还没有注册成功，需要判断是否符合注册资格
            // savedHelpType 缺失时不走完成分支：先留在补问屏，答完后在 handleHelpTypeChange 里续上完成流程
            if (savedHelpType && +data.data.step === totalSteps) {
                setIsCompleted(true);
                // 使用传入的currentHasAccount参数，如果没有则使用checkEmailData中的值
                const accountExists =
                    currentHasAccount !== undefined
                        ? currentHasAccount
                        : checkEmailData?.providerTypes !== null &&
                          Array.isArray(checkEmailData?.providerTypes) &&
                          (checkEmailData?.providerTypes?.length || 0) > 0;
                evaluateQualificationAndMaybeStop(result, accountExists);
            }
        } else {
            handleEmailNext({
                ...formData,
                email,
            });
        }
    };

    const handleEmailSubmit = async (email: string) => {
        const nextData = { ...formData, email };
        if (isCheckingEmail) {
            return;
        }
        if (isGetAccountData && email === checkEmail) {
            handleEmailNext(nextData);
            return;
        }
        setCheckEmail(email);

        // 邮箱提交埋点
        trackHeapEvent('WebApp - PPA Lead Form - Submit - Email', {
            em: await encryptString(email),
            step: currentStep,
        });

        try {
            setIsCheckingEmail(true);
            // 开始检查邮箱埋点
            trackHeapEvent('WebApp - PPA Lead Form - Click - Email Check', {
                em: await encryptString(email),
            });

            const { data: leadFormData } = await checkLeadFormByEmail({ email });
            setCheckEmailData(leadFormData.data);

            const hasAccountValue =
                leadFormData.data.providerTypes !== null &&
                Array.isArray(leadFormData.data.providerTypes) &&
                leadFormData.data.providerTypes.length > 0;

            setHasAccount(hasAccountValue);
            setIsCheckingEmail(false);

            // 邮箱检查结果埋点
            trackHeapEvent('WebApp - PPA Lead Form - View - Email Check Result', {
                em: await encryptString(email),
                hasAccount: hasAccountValue,
                isListedOnKlarity: leadFormData.data.isListedOnKlarity,
                hasLeadForm: !!leadFormData.data.leadForm,
                ppv: leadFormData.data.ppv,
            });

            if (leadFormData && leadFormData.code === 200) {
                // 新账号继续走流程，providerTypes 什么时候有值： 对于kiwi历史账号或者klarity端调用registerAndLogin接口，会返回providerTypes
                if (!leadFormData.data.providerTypes) {
                    getLeadFormInfoByEmail(email, hasAccountValue);
                    return;
                }

                // PPV 跳转到dashboard
                if (leadFormData.data.ppv) {
                    trackHeapEvent('WebApp - PPA Lead Form - View - Existing Account Redirect', {
                        em: await encryptString(email),
                        reason: 'ppv',
                        redirectTo: 'dashboard',
                    });
                    navigator(PATH.PPA_LOGIN, {
                        state: { redirect: PATH.DASHBOARD, email },
                    });
                    return;
                }

                // isListedOnKlarity 是否历史帐号，是否上架过
                if (leadFormData.data.isListedOnKlarity) {
                    trackHeapEvent('WebApp - PPA Lead Form - View - Existing Account Redirect', {
                        em: await encryptString(email),
                        reason: 'isListedOnKlarity',
                        redirectTo: 'dashboard',
                    });
                    navigator(PATH.PPA_LOGIN, {
                        state: { redirect: PATH.DASHBOARD, email },
                    });
                    return;
                }

                // kiwi only && lead form && 没走完onboarding
                if (
                    leadFormData.data.leadForm &&
                    leadFormData.data.leadForm.step === totalSteps &&
                    leadFormData.data.onBoardingStep !== EPPAStep.STEP_21
                ) {
                    trackHeapEvent('WebApp - PPA Lead Form - View - Existing Account Redirect', {
                        em: await encryptString(email),
                        reason: 'incomplete_onboarding',
                        redirectTo: 'onboarding_steps',
                    });
                    navigator(PATH.PPA_LOGIN, {
                        state: { redirect: PATH.PPA_ONBOARDING_STEPS, email },
                    });
                }

                // 1. 新kiwi only 账号 2. 新账号登录过没走完lead form 3. 老账号登录过走完lead form
                // 注意：这里需要传递hasAccountValue而不是依赖状态
                getLeadFormInfoByEmail(email, hasAccountValue);
            }
        } catch (error) {
            // eslint-disable-next-line no-console
            console.error('checkLeadFormByEmail API调用失败:', error);
            // API 错误埋点
            trackHeapEvent('WebApp - PPA Lead Form - View - API Error', {
                em: await encryptString(email),
                errorType: 'checkLeadFormByEmail',
                error: error instanceof Error ? error.message : 'Unknown error',
            });
            setCheckEmailData(null);
            setIsCheckingEmail(false);
        }
    };

    const handleHelpTypeChange = async (helpType: string) => {
        const nextData = { ...formData, practiceHelpType: helpType };
        setFormData(nextData);

        trackHeapEvent('WebApp - PPA Lead Form - Click - Help Type Select', {
            helpType,
            step: currentStep,
        });

        // 补问场景不能把 step 写回 2：老账号 step=9 会被降级，
        // 后续 checkLeadFormByEmail 里 step === totalSteps 的完成判断全部失效
        await saveStepData(Math.max(2, nextData.step || 2), nextData);

        const resumeStep = helpTypeResumeStepRef.current;
        if (resumeStep != null) {
            helpTypeResumeStepRef.current = null;
            if (resumeStep > totalSteps) {
                // 已完成问卷的老账号：补答后续上原本的完成流程
                setIsCompleted(true);
                evaluateQualificationAndMaybeStop(nextData);
            } else {
                setCurrentStep(resumeStep);
            }
        } else {
            setCurrentStep(3);
        }
    };

    const handleServicesChange = async (specialties: string[]) => {
        const nextData = { ...formData, specialties };
        setFormData(nextData);

        // 服务选择埋点
        trackHeapEvent('WebApp - PPA Lead Form - Click - Services Select', {
            specialties,
            step: currentStep,
        });

        await saveStepData(3, nextData);

        // 数据保存成功后跳转到下一步（credentialing 也走完整问卷流程，仅 Active Account 文案与注册后跳转不同）
        setCurrentStep(4);
    };

    const handleLicensesChange = async (licenses: string[]) => {
        const nextData = { ...formData, states: licenses.join(',') };
        setFormData(nextData);
        await saveStepData(4, nextData);
    };

    const handlePracticeSizeChange = async (practiceSize: string) => {
        const nextData = { ...formData, providerCount: practiceSize };
        setFormData(nextData);
        await saveStepData(5, nextData);
        setCurrentStep(6);
    };

    const handlePatientVolumeChange = async (patientVolume: string) => {
        const nextData = { ...formData, patientsPerMonth: patientVolume };
        setFormData(nextData);
        await saveStepData(6, nextData);
        setCurrentStep(7);
    };

    const handleReferralSourceChange = async (referralSource: string) => {
        const nextData = { ...formData, referralSource };
        setFormData(nextData);
        await saveStepData(7, nextData);
        setCurrentStep(8);
    };

    const handleBillingMethodChange = async (billingMethod: string) => {
        const nextData = { ...formData, billingMethod };
        setFormData(nextData);
        await saveStepData(8, nextData);
        setCurrentStep(9);
    };

    const handleEhrAccessChange = async (ehrAccess: string) => {
        const nextData = { ...formData, ehrSystemStatus: ehrAccess };
        setFormData(nextData);
        await saveStepData(9, nextData);

        // 选了 No → 跳到 NoEhrInfoStep(step 10)
        if (ehrAccess === 'no-not-planned') {
            setCurrentStep(10);
            return;
        }

        // 选了 Yes → 走原流程
        setIsCompleted(true);
        evaluateQualificationAndMaybeStop(nextData, hasAccount);
    };

    const handleNoEhrContinue = async () => {
        setIsCompleted(true);
        evaluateQualificationAndMaybeStop(formData, hasAccount);
    };

    const handleNext = async () => {
        // Heap only: track next click without changing submission logic
        trackHeapEvent('WebApp - PPA Lead Form - Click - Next', {
            step: currentStep,
            totalSteps,
        });

        if (currentStep < totalSteps) {
            setCurrentStep(currentStep + 1);
        } else if (currentStep === totalSteps) {
            // Onboarding completed
            trackHeapEvent('WebApp - PPA Lead Form - View - Complete', {
                step: currentStep,
                totalSteps,
            });
        }
    };

    const handleBack = () => {
        if (currentStep > 1) {
            setCurrentStep(currentStep - 1);
        }
    };

    const handleChooseAgain = () => {
        setIsCompleted(false);
        setCurrentStep(totalSteps);
        setIsNotQualified(false);
    };

    const handleChangeEmail = () => {
        setIsCompleted(false);
        setCurrentStep(1);
        setIsNotQualified(false);
    };

    const handleChatVisibleChange = (show: boolean) => {
        setIsChatVisible(show);
    };

    useEffect(() => {
        setCurrentPlatformType(ERegisterType.KLARITY_PORTAL);
        // 页面访问埋点
        trackHeapEvent('WebApp - PPA Lead Form - View - Page', {
            step: currentStep,
            hasEmail: !!urlEmail,
            action: urlAction || 'normal',
        });
        // eslint-disable-next-line react-hooks/exhaustive-deps
    }, []);

    useEffect(() => {
        if (urlEmail) {
            // 如果指定 action=signup，直接显示注册页
            if (urlAction === 'signup') {
                setFormData((prev) => ({ ...prev, email: urlEmail }));
                setIsNotQualified(false);
                setIsCompleted(true);
                return;
            }

            // 更新formData中的邮箱
            setFormData((prev) => ({ ...prev, email: urlEmail }));

            // 如果action=reselect，通过接口获取预填数据并跳转到第7步
            if (urlAction === 'reselect') {
                trackHeapEvent('WebApp - PPA Lead Form - View - Reselect Action', {
                    email: urlEmail,
                });
                getLeadFormInfoByEmail(urlEmail);
            }
        }
        // 未提供 email 但 action=signup 的情况，仍直接展示注册页
        if (!urlEmail && urlAction === 'signup') {
            setIsNotQualified(false);
            setIsCompleted(true);
        }
        // eslint-disable-next-line react-hooks/exhaustive-deps
    }, [urlEmail, urlAction]);

    // 步骤变化埋点
    useEffect(() => {
        trackHeapEvent('WebApp - PPA Lead Form - View - Step', {
            step: currentStep,
            totalSteps,
        });
    }, [currentStep]);

    const getAIChatPageId = (step: number): EAIChatPageId => {
        switch (step) {
            case 1:
                return EAIChatPageId.PROVIDER_ONBOARDING_EMAIL_ENTRY;
            case 2:
                return EAIChatPageId.PROVIDER_ONBOARDING_HELP_TYPE;
            case 3:
                return EAIChatPageId.PROVIDER_ONBOARDING_SERVICES_SELECTION;
            case 4:
                return EAIChatPageId.PROVIDER_ONBOARDING_STATE_LICENSING;
            case 5:
                return EAIChatPageId.PROVIDER_ONBOARDING_FINISH;
            case 6:
                return EAIChatPageId.PROVIDER_ONBOARDING_PATIENT_VOLUME;
            case 7:
                return EAIChatPageId.PROVIDER_ONBOARDING_REFERRAL_SOURCE;
            case 8:
                return EAIChatPageId.PROVIDER_ONBOARDING_BILLING_METHOD;
            case 9:
                return EAIChatPageId.PROVIDER_ONBOARDING_EHR_ACCESS;
            case 10:
                return EAIChatPageId.PROVIDER_ONBOARDING_NO_EHR_INFO;
            default:
                return EAIChatPageId.PROVIDER_ONBOARDING_EMAIL_ENTRY;
        }
    };

    const renderCurrentStep = () => {
        switch (currentStep) {
            case 1:
                return (
                    // <LicenseSelectionStep
                    //     onLicensesChange={handleLicensesChange}
                    //     onBack={handleBack}
                    //     onNext={handleNext}
                    //     currentStep={currentStep}
                    //     totalSteps={totalSteps}
                    //     initialLicenses={
                    //         formData.states
                    //             ? formData.states.split(',').filter(Boolean)
                    //             : []
                    //     }
                    // />
                    <EmailOnboardingStep
                        onEmailSubmit={handleEmailSubmit}
                        currentStep={currentStep}
                        totalSteps={totalSteps}
                        initialEmail={formData.email}
                        isCheckingEmail={isCheckingEmail}
                    />
                );
            case 2:
                return (
                    <HelpTypeStep
                        onHelpTypeChange={handleHelpTypeChange}
                        onBack={handleBack}
                        onNext={handleNext}
                        currentStep={currentStep}
                        totalSteps={totalSteps}
                        initialHelpType={formData.practiceHelpType}
                    />
                );
            case 3:
                return (
                    <ServiceSelectionStep
                        onServicesChange={handleServicesChange}
                        onBack={handleBack}
                        onNext={handleNext}
                        currentStep={currentStep}
                        totalSteps={totalSteps}
                        initialServices={formData.specialties}
                        initialOthersText=""
                    />
                );
            case 4:
                return (
                    <LicenseSelectionStep
                        onLicensesChange={handleLicensesChange}
                        onBack={handleBack}
                        onNext={handleNext}
                        currentStep={currentStep}
                        totalSteps={totalSteps}
                        initialLicenses={formData.states ? formData.states.split(',').filter(Boolean) : []}
                    />
                );
            case 5:
                return (
                    <PracticeSizeStep
                        onPracticeSizeChange={handlePracticeSizeChange}
                        onBack={handleBack}
                        onNext={handleNext}
                        currentStep={currentStep}
                        totalSteps={totalSteps}
                        initialPracticeSize={formData.providerCount}
                    />
                );
            case 6:
                return (
                    <PatientVolumeStep
                        onPatientVolumeChange={handlePatientVolumeChange}
                        onBack={handleBack}
                        onNext={handleNext}
                        currentStep={currentStep}
                        totalSteps={totalSteps}
                        initialPatientVolume={formData.patientsPerMonth}
                    />
                );
            case 7:
                return (
                    <ReferralSourceStep
                        onReferralSourceChange={handleReferralSourceChange}
                        onBack={handleBack}
                        onNext={handleNext}
                        currentStep={currentStep}
                        totalSteps={totalSteps}
                        initialReferralSource={formData.referralSource}
                    />
                );
            case 8:
                return (
                    <BillingMethodStep
                        onBillingMethodChange={handleBillingMethodChange}
                        onBack={handleBack}
                        onNext={handleNext}
                        currentStep={currentStep}
                        totalSteps={totalSteps}
                        initialBillingMethod={formData.billingMethod}
                    />
                );
            case 9:
                return (
                    <EhrAccessStep
                        onEhrAccessChange={handleEhrAccessChange}
                        onBack={handleBack}
                        onNext={handleNext}
                        currentStep={currentStep}
                        totalSteps={totalSteps}
                        initialEhrAccess={formData.ehrSystemStatus}
                    />
                );
            case 10:
                return (
                    <NoEhrInfoStep
                        onBack={handleBack}
                        onNext={handleNoEhrContinue}
                        currentStep={totalSteps}
                        totalSteps={totalSteps}
                    />
                );
            default:
                return (
                    <EmailOnboardingStep
                        onEmailSubmit={handleEmailSubmit}
                        currentStep={currentStep}
                        totalSteps={totalSteps}
                        initialEmail={formData.email}
                    />
                );
        }
    };

    const renderLeadForm = () => (
        <div className={s.wrap}>
            <PPAOnboardingHeader />
            <div className={s.content}>
                <div className={s.contentWrap}>
                    <div className={`${s.contentIn}${currentStep === 2 ? ` ${s.contentInWide}` : ''}`}>
                        <div className={s.stepIn}>
                            <ConfigProvider
                                theme={{
                                    token: {
                                        colorPrimary: '#0E62F4',
                                    },
                                }}
                            >
                                {renderCurrentStep()}
                            </ConfigProvider>
                        </div>
                    </div>
                    <div className={s.chatWrap}>
                        <AIChat
                            pageId={getAIChatPageId(currentStep)}
                            defaultShow={!isCompleted && !isMobile()}
                            onVisableChange={handleChatVisibleChange}
                        />
                    </div>
                </div>
            </div>
        </div>
    );

    const renderNotQualified = () => {
        return (
            <div className={s.wrap}>
                <PPAOnboardingHeader />
                <div className={s.content}>
                    <div className={cx(s.contentWrap, isChatVisible ? '' : s.contentWrapNotQualified)}>
                        <div className={cx(s.contentIn, s.contentInNotQualified)}>
                            <div className={s.stepIn}>
                                <UserNotQualified onBack={handleChooseAgain} />
                            </div>
                        </div>
                        <div className={s.chatWrap}>
                            <AIChat
                                pageId={getAIChatPageId(currentStep)}
                                defaultShow={!isCompleted && !isMobile()}
                                onVisableChange={handleChatVisibleChange}
                            />
                        </div>
                    </div>
                </div>
            </div>
        );
    };

    return (
        <>
            {isCompleted && isNotQualified && renderNotQualified()}
            {isCompleted && !isNotQualified && (
                <SignUp
                    leadFormData={formData}
                    onChangeEmail={handleChangeEmail}
                    emailDisabled={urlAction !== 'signup'}
                />
            )}
            {!isCompleted && renderLeadForm()}
        </>
    );
}
